/**
 * MerchantHealthScorecard — a composite 0-100 "how healthy is my store?"
 * gauge with 4-way breakdown. Clover shows raw sales but doesn't tell
 * you if you're actually improving or slipping. This does.
 *
 * Lives at the top of the Grocery Dashboard tab.
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus, Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human-readable labels for the 4 sub-dimensions
const SUB_LABELS = {
  sales_velocity: "Sales Velocity",
  margin_health: "Margin Health",
  waste: "Waste Control",
  stock_coverage: "Stock Coverage",
};

function scoreColor(s) {
  if (s === null || s === undefined) return "#94a3b8";
  if (s >= 90) return "#10b981"; // emerald
  if (s >= 80) return "#22c55e"; // green
  if (s >= 65) return "#eab308"; // yellow
  if (s >= 50) return "#f97316"; // orange
  return "#ef4444";              // red
}

function gradeCopy(grade) {
  return {
    A: "Excellent",
    B: "Strong",
    C: "Steady",
    D: "Needs work",
    F: "At risk",
  }[grade] || "";
}

// Small SVG ring gauge — no chart lib dep
function ScoreGauge({ score, size = 132 }) {
  const R = 56;
  const CIRC = 2 * Math.PI * R;
  const pct = Math.max(0, Math.min(100, score ?? 0)) / 100;
  const dash = CIRC * pct;
  const color = scoreColor(score);
  return (
    <svg width={size} height={size} viewBox="0 0 132 132" style={{ display: "block" }}>
      {/* Background ring */}
      <circle
        cx="66" cy="66" r={R}
        fill="none" stroke="currentColor" strokeOpacity="0.10"
        strokeWidth="10"
      />
      {/* Progress ring — rotates to start at 12 o'clock */}
      <circle
        cx="66" cy="66" r={R}
        fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
        strokeDasharray={`${dash} ${CIRC}`}
        transform="rotate(-90 66 66)"
        style={{ transition: "stroke-dasharray 700ms ease" }}
      />
      <text
        x="66" y="66" textAnchor="middle" dominantBaseline="central"
        fontSize="34" fontWeight="700" fill={color}
        fontFamily="ui-monospace,SFMono-Regular,Menlo,monospace"
      >
        {score ?? "—"}
      </text>
      <text
        x="66" y="92" textAnchor="middle"
        fontSize="10" fill="currentColor" opacity="0.5" letterSpacing="1"
      >
        / 100
      </text>
    </svg>
  );
}

export default function MerchantHealthScorecard({ businessSlug, isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/grocery/b/${businessSlug}/health-score`);
        if (!res.ok) throw new Error("fetch failed");
        const j = await res.json();
        if (alive) setData(j);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [businessSlug]);

  const cardBg = isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200";
  const heading = isDark ? "text-white" : "text-slate-900";
  const sub = isDark ? "text-slate-400" : "text-slate-500";

  if (loading) {
    return (
      <Card className={cardBg} data-testid="merchant-health-loading">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 text-sm">
            <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            <span className={sub}>Calculating health score…</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Insufficient-data state — clean, actionable
  if (!data || data.grade === "insufficient_data") {
    return (
      <Card className={cardBg} data-testid="merchant-health-empty">
        <CardContent className="p-5 flex items-center gap-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
            isDark ? "bg-slate-700" : "bg-slate-100"
          }`}>
            <Activity className={`w-7 h-7 ${sub}`} />
          </div>
          <div className="flex-1">
            <p className={`text-sm font-semibold ${heading}`}>Merchant Health Score</p>
            <p className={`text-xs mt-0.5 ${sub}`}>
              {data?.top_driver || "Connect Clover and record a few sales to unlock your score."}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { score, grade, breakdown, delta_from_last_week: delta, top_driver } = data;
  const color = scoreColor(score);
  const DeltaIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const deltaColor = delta > 0
    ? (isDark ? "text-emerald-400" : "text-emerald-600")
    : delta < 0
      ? (isDark ? "text-rose-400" : "text-rose-600")
      : sub;

  return (
    <Card className={cardBg} data-testid="merchant-health-card">
      <CardContent className="p-5">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
          {/* Left: the gauge */}
          <div className={isDark ? "text-slate-100" : "text-slate-700"}>
            <ScoreGauge score={score} />
          </div>

          {/* Middle: grade + driver + delta */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold uppercase tracking-wider ${sub}`}>
                Merchant Health Score
              </span>
              <span
                className="text-[10px] px-1.5 py-0.5 rounded font-mono font-semibold"
                style={{ backgroundColor: `${color}22`, color: color }}
              >
                {gradeCopy(grade)}
              </span>
            </div>
            <p className={`text-lg font-semibold mt-1 ${heading}`} data-testid="merchant-health-driver">
              {top_driver}
            </p>
            {delta !== null && delta !== undefined && (
              <div className={`flex items-center gap-1.5 mt-2 text-sm ${deltaColor}`} data-testid="merchant-health-delta">
                <DeltaIcon className="w-4 h-4" />
                <span className="font-medium">
                  {delta > 0 ? "+" : ""}{delta} pts vs last week
                </span>
              </div>
            )}
          </div>

          {/* Right: 4 sub-scores */}
          <div className="grid grid-cols-2 gap-2 w-full md:w-72" data-testid="merchant-health-breakdown">
            {Object.entries(breakdown || {}).map(([key, sub_data]) => {
              const c = scoreColor(sub_data.score);
              return (
                <div
                  key={key}
                  className={`p-2.5 rounded-md ${isDark ? "bg-slate-900/40" : "bg-slate-50"}`}
                  data-testid={`merchant-health-sub-${key}`}
                >
                  <p className={`text-[10px] uppercase tracking-wide ${sub}`}>
                    {SUB_LABELS[key] || key}
                  </p>
                  <div className="flex items-baseline gap-1.5">
                    <span
                      className="font-mono text-base font-semibold"
                      style={{ color: c }}
                    >
                      {sub_data.score}
                    </span>
                    <span className={`text-[10px] ${sub}`}>/ 100</span>
                  </div>
                  <div
                    className="h-1 rounded-full mt-1 overflow-hidden"
                    style={{ backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}
                  >
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${sub_data.score}%`,
                        backgroundColor: c,
                        transition: "width 700ms ease",
                      }}
                    />
                  </div>
                  <p className={`text-[10px] mt-1 truncate ${sub}`} title={sub_data.value}>
                    {sub_data.value}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
